// src/components/dashboard/mastercard/MastercardCardSection.tsx
import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { useDashboard } from "../../../context/DashboardContext";
import { Link } from 'react-router-dom';
import { MastercardIcon } from "../../ui/MastercardIcon";
import { VisaIcon } from "../../ui/visacardIcon";
import {NewCardIcon} from "../../ui/NewCardIcon";
import "../../../assets/styles/wallet-card.css";
import "../../../assets/styles/mastercard-card.css";
import { MastercardDataCompletionForm } from "./MastercardDataCompletionForm";
import { useWindow } from '../../../context/WindowContext';
import { verestroService } from '../../../services/api/verestro.service';
import { TermsAndConditionsModal } from './TermsAndConditionsModal';
import { getCurrentApiUrl } from '../../../config/server.config';
// Interfaces for typing
interface MastercardCard {
  id: string;
  cardId: string;
  currency: string;
  status: string;
  balance: string;
  cardNumber?: string;
  [key: string]: string | undefined;
}

// Props for MastercardCardSection component
interface MastercardCardSectionProps {
  cards?: MastercardCard[];
}

// Key for storing data in localStorage - now will include user ID
const CARD_CACHE_KEY_PREFIX = 'mastercard-card-data';
const CARD_CACHE_TIMESTAMP_KEY_PREFIX = 'mastercard-card-data-timestamp';
const CARD_CACHE_EXPIRY = 15 * 60 * 1000; // 15 minutes

export function MastercardCardSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user, isFullOnboarding } = useAuth();
  const { refreshDashboard } = useDashboard();
  const userId = user?.id || 'guest'; // Use 'guest' as fallback
  const { windowWidth } = useWindow();
  // Create user-specific cache keys
  const CARD_CACHE_KEY = `${CARD_CACHE_KEY_PREFIX}-${userId}`;
  const CARD_CACHE_TIMESTAMP_KEY = `${CARD_CACHE_TIMESTAMP_KEY_PREFIX}-${userId}`;

  const [creatingCard, setCreatingCard] = useState<boolean>(false);
  const [newlyCreatedCardId, setNewlyCreatedCardId] = useState<string | null>(null);

  // State for MasterCard form
  const [showMastercardForm, setShowMastercardForm] = useState<boolean>(false);
  const [mastercardFormData, setMastercardFormData] = useState<any>(null);
  const [isCheckingMastercardData, setIsCheckingMastercardData] = useState<boolean>(false);
  const [balances, setBalances] = useState<Record<string, string>>({});
  const [currencies, setCurrencies] = useState<Record<string, string>>({});

  // State for card creation process
  const [creationStep, setCreationStep] = useState<number>(0);
  const [creationMessage, setCreationMessage] = useState<string>('');
  const [showBlockingOverlay, setShowBlockingOverlay] = useState<boolean>(false);
  const [missingRequirements, setMissingRequirements] = useState<string[]>([]);
  const [showRequirementsModal, setShowRequirementsModal] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);

  // State to control card existence check
  const [hasCheckedCardExistence, setHasCheckedCardExistence] = useState<boolean>(false);
  const [userHasCard, setUserHasCard] = useState<boolean>(false);
  const [cardDetailsFromDB, setCardDetailsFromDB] = useState<any>(null);

  // Function to load card data from cache
  const loadCardDataFromCache = () => {
    try {
      const cachedData = localStorage.getItem(CARD_CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(CARD_CACHE_TIMESTAMP_KEY);

      if (cachedData && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp);
        const now = Date.now();

        // Check if cache is still valid (less than 15 minutes)
        if (now - timestamp < CARD_CACHE_EXPIRY) {
          const parsedData = JSON.parse(cachedData);

          // Set user has card status based on cache
          setUserHasCard(parsedData.hasCard);

          // If user has a card, set the details
          if (parsedData.hasCard && parsedData.cardDetails) {
            setCardDetailsFromDB(parsedData.cardDetails);
            setBalances(parsedData.balances || {});
            setCurrencies(parsedData.currencies || {});
          } else {
            // Make sure we clear any previous card data
            setCardDetailsFromDB(null);
            setBalances({});
            setCurrencies({});
          }

          setHasCheckedCardExistence(true);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error loading card data from cache:', error);
      return false;
    }
  };

  // Function to save card data to cache
  const saveCardDataToCache = (hasCard: boolean, cardDetails: any, balancesData: Record<string, string>, currenciesData: Record<string, string>) => {
    try {
      const dataToCache = {
        hasCard,
        cardDetails,
        balances: balancesData,
        currencies: currenciesData,
        timestamp: Date.now()
      };

      localStorage.setItem(CARD_CACHE_KEY, JSON.stringify(dataToCache));
      localStorage.setItem(CARD_CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Error saving card data to cache:', error);
    }
  };

  // When initializing the component, try to load data from cache
  useEffect(() => {
    if (!userId || userId === 'guest') {
      // Don't load from cache if we don't have a valid user ID
      setHasCheckedCardExistence(false);
      return;
    }

    // Force a clean state when user changes
    setUserHasCard(false);
    setCardDetailsFromDB(null);

    // Try loading from cache first for faster UI response
    const cardDataLoaded = loadCardDataFromCache();
    if (!cardDataLoaded) {
      // If no data in cache, continue with normal verification
      setHasCheckedCardExistence(false);
    } else {
    }

    // Force a refetch from backend regardless of cache to ensure data is current
    refetchUserCard();
  }, [userId]); // Re-run when userId changes

  // Clear state when user changes
  useEffect(() => {
    // Clear all our state indicators when user changes
    // This addresses any potential state persistence between user switches
    setUserHasCard(false);
    setCardDetailsFromDB(null);
    setBalances({});
    setCurrencies({});
    setHasCheckedCardExistence(false);
    setNewlyCreatedCardId(null);

    // Force refetch when user changes to get the correct data
    if (userId && userId !== 'guest') {
      queryClient.invalidateQueries({ queryKey: ['user-mastercard-card'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['mastercard-cards'] });
    }
  }, [userId, queryClient]);

  // Query to check if user has a card in Verestro
  const { data: userCardData, isLoading: isCheckingDatabase, refetch: refetchUserCard } = useQuery({
    queryKey: ['user-mastercard-card', userId], // Include userId in the query key
    queryFn: async () => {
      // Skip API call if there's no valid user
      if (!userId || userId === 'guest') {
        setUserHasCard(false);
        return null;
      }

      try {
        // Check if the user has a card
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) {
          console.error("No authentication token found");
          return null;
        }

        const response = await fetch(`${getCurrentApiUrl()}/providers/verestro/cards`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {

          // If query fails, try to use cached data
          const cacheLoaded = loadCardDataFromCache();
          if (cacheLoaded && userHasCard) {
            console.log(`Using cached card data for user ${userId}`);
            return cardDetailsFromDB;
          }
          return null;
        }

        const data = await response.json();

        // Check if there are cards in the response
        if (data.success && data.data && data.data.data && Array.isArray(data.data.data) && data.data.data.length > 0) {
          // User has at least one card
          setUserHasCard(true);
          // Use the first card found
          const firstCard = data.data.data[0];
          setCardDetailsFromDB(firstCard);

          // Save to cache for future use
          saveCardDataToCache(true, firstCard, balances, currencies);

          return firstCard;
        } else {
          setUserHasCard(false);
          setCardDetailsFromDB(null);
          // Also save this result to cache
          saveCardDataToCache(false, null, {}, {});
          return null;
        }
      } catch (error) {

        // If there's an error, try to use cached data
        const cacheLoaded = loadCardDataFromCache();
        if (cacheLoaded && userHasCard) {

          return cardDetailsFromDB;
        }
        setUserHasCard(false);
        setCardDetailsFromDB(null);
        return null;
      } finally {
        setHasCheckedCardExistence(true);
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - increased to avoid frequent queries
    gcTime: 15 * 60 * 1000, // 15 minutes (previously called cacheTime)
    refetchOnWindowFocus: true, // Re-enable automatic refreshes on window focus
    refetchOnMount: true, // Always refresh when the component mounts
    refetchOnReconnect: true, // Re-enable refresh when connection is restored
    enabled: !!userId && userId !== 'guest' // Only run query if we have a valid user
  });

  // Effect to get card balance when card details change
  useEffect(() => {
    if (cardDetailsFromDB && cardDetailsFromDB.id) {
      // Always fetch the latest balance when cardDetailsFromDB changes
      fetchCardBalanceFromAPI(cardDetailsFromDB.id);
    }
  }, [cardDetailsFromDB]);

  // Track when userHasCard changes
  // useEffect(() => {
  //   console.log(`userHasCard changed to: ${userHasCard}`);
  // }, [userHasCard]);

  // Function to get card balance
  const fetchCardBalanceFromAPI = async (cardId: string) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return;
      }

      const response = await fetch(`${getCurrentApiUrl()}/providers/verestro/cards/${cardId}/balance`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();

      // Extract balance using the correct structure
      if (data.success && data.data) {
        const newBalances = {
          ...balances,
          [cardId]: data.data.balance.toString()
        };

        const newCurrencies = {
          ...currencies,
          [cardId]: data.data.currency || 'EUR'
        };

        setBalances(newBalances);
        setCurrencies(newCurrencies);

        // Update cache with new data
        saveCardDataToCache(userHasCard, cardDetailsFromDB, newBalances, newCurrencies);
      }
    } catch (error) {
      console.error(`Error fetching details for card ${cardId}:`, error);
    }
  };

  // Check MasterCard requirements
  const checkMastercardRequirements = async () => {
    setIsCheckingMastercardData(true);

    try {
      // Check if user has all requirements
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        toast.error('Please login to continue');
        return;
      }

      // Get user data from /api/auth/me
      const meResponse = await fetch(
        `${getCurrentApiUrl()}/auth/me`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!meResponse.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const meData = await meResponse.json();
      const user = meData.user || {};
      const userData = user.profile || {};

      // Debug log to see what data we have
      console.log('User data received:', user);
      console.log('Profile data:', userData);

      // Also get documents
      const docsResponse = await fetch(
        `${getCurrentApiUrl()}/profile/documents`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const documents = docsResponse.ok ? await docsResponse.json() : { data: { documents: [] } };
      // The response has structure: { data: { documents: [...], total: 5 } }
      const userDocs = documents.data?.documents || documents.documents || [];
      const missing: string[] = [];

      // Check account type - accountType is on the user, not on the profile!
      if (user.accountType !== 'INDIVIDUAL') {
        toast.error('Only INDIVIDUAL accounts can create MasterCards. EXPRESS accounts are not eligible.');
        console.log('Account type check failed. User accountType:', user.accountType);
        return;
      }

      // Check required fields - using correct field names from UserProfile schema
      const requiredFields = [
        { field: 'firstName', label: 'First Name' },
        { field: 'lastName', label: 'Last Name' },
        { field: 'birthDate', label: 'Date of Birth' }, // birthDate not dateOfBirth
        { field: 'citizenship', label: 'Nationality' }, // citizenship not nationality
        { field: 'phone', label: 'Phone Number' },
        { field: 'addressLine1', label: 'Address' }, // addressLine1 not address
        { field: 'city', label: 'City' },
        { field: 'postalCode', label: 'Postal Code' },
        { field: 'country', label: 'Country' }
      ];

      for (const { field, label } of requiredFields) {
        if (!userData[field]) {
          missing.push(label);
          console.log(`Missing field: ${field} (${label}), value:`, userData[field]);
        } else {
          console.log(`✓ Has field: ${field} (${label}), value:`, userData[field]);
        }
      }

      // Check documents - using correct document types
      console.log('Documents received:', userDocs);

      const hasIdDocs = userDocs.some((doc: any) => {
        const hasId = doc.documentType === 'ID_CARD_FRONT' ||
                      doc.documentType === 'ID_FRONT' ||
                      doc.documentType === 'PASSPORT';
        if (hasId) console.log('✓ Has ID document:', doc.documentType);
        return hasId;
      });

      const hasSelfie = userDocs.some((doc: any) => {
        const hasSel = doc.documentType === 'SELFIE_WITH_ID' ||
                       doc.documentType === 'SELFIE';
        if (hasSel) console.log('✓ Has selfie:', doc.documentType);
        return hasSel;
      });

      const hasProofOfAddress = userDocs.some((doc: any) => {
        const hasProof = doc.documentType === 'UTILITY_BILL' ||
                         doc.documentType === 'BANK_STATEMENT';
        if (hasProof) console.log('✓ Has proof of address:', doc.documentType);
        return hasProof;
      });

      if (!hasIdDocs) missing.push('Identity document (ID/Passport)');
      if (!hasSelfie) missing.push('Selfie verification');
      if (!hasProofOfAddress) missing.push('Proof of address');

      if (missing.length > 0) {
        // Show requirements modal
        setMissingRequirements(missing);
        setShowRequirementsModal(true);
        return;
      }

      // All requirements met, show terms modal first
      setShowTermsModal(true);
    } catch (error) {
      console.error('Error checking requirements:', error);
      toast.error('Error checking requirements. Please try again.');
    } finally {
      setIsCheckingMastercardData(false);
    }
  };

  // Start automatic card creation with delays
  const startAutomaticCardCreation = async () => {
    try {
      setShowBlockingOverlay(true);
      setCreationStep(1);
      setCreationMessage('Creating digital wallet...');

      // Step 1: Create Wallet
      const walletResponse = await verestroService.createWallet();
      if (!walletResponse.success) {
        throw new Error(walletResponse.message || 'Failed to create wallet');
      }
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Step 2: Accept Terms
      setCreationStep(2);
      setCreationMessage('Accepting terms and conditions...');
      const termsResponse = await verestroService.acceptTermsAndConditions();
      if (!termsResponse.success) {
        throw new Error(termsResponse.message || 'Failed to accept terms');
      }
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Step 3: KYC Verification
      setCreationStep(3);
      setCreationMessage('Processing KYC verification...');
      const verificationResponse = await verestroService.createVerification();
      if (!verificationResponse.success) {
        throw new Error(verificationResponse.message || 'KYC verification failed');
      }
      await new Promise(resolve => setTimeout(resolve, 5000)); // Longer delay for KYC

      // Step 4: Create Balance
      setCreationStep(4);
      setCreationMessage('Setting up EUR balance...');
      const balanceResponse = await verestroService.createBalance();
      if (!balanceResponse.success) {
        throw new Error(balanceResponse.message || 'Failed to create balance');
      }
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Step 5: Create Card
      setCreationStep(5);
      setCreationMessage('Creating your MasterCard...');
      const cardResponse = await verestroService.createCard(
        balanceResponse.data.balanceId,
        'BELOBABA CARD'
      );
      if (!cardResponse.success) {
        throw new Error(cardResponse.message || 'Failed to create card');
      }
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Success!
      setCreationMessage('MasterCard created successfully!');
      toast.success('🎉 Your MasterCard has been created successfully!');

      // Update state
      setUserHasCard(true);
      setCardDetailsFromDB(cardResponse.data);

      // Clear ALL cache and invalidate ALL queries
      localStorage.removeItem(CARD_CACHE_KEY);
      localStorage.removeItem(CARD_CACHE_TIMESTAMP_KEY);

      // Invalidate all related queries to force refetch
      queryClient.invalidateQueries({ queryKey: ['user-mastercard-card'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['mastercard-cards'] });

      // Force refetch
      await refetchUserCard();

      // Fetch balance for the new card
      if (cardResponse.data?.cardId) {
        await fetchCardBalanceFromAPI(cardResponse.data.cardId);
      }

      // Refresh dashboard to update totals
      refreshDashboard();

      // Hide overlay after a moment
      setTimeout(() => {
        setShowBlockingOverlay(false);
        setCreationStep(0);
        setCreationMessage('');
      }, 2000);

    } catch (error: any) {
      console.error('Card creation error:', error);
      toast.error(error.message || 'Failed to create MasterCard');
      setShowBlockingOverlay(false);
      setCreationStep(0);
      setCreationMessage('');
    }
  };

  // Handle MasterCard form submission (legacy - only for backward compatibility)
  const handleMastercardFormSubmit = async (data: any) => {
    try {
      setCreatingCard(true);
      console.log(data);
      // Check if this is a temporary request for fast onboarding users
      if (data.temporaryMessage) {
        // For now, we just show a success message and don't proceed with the card creation
        toast.success(data.temporaryMessage);

        // Hide form
        setShowMastercardForm(false);
        setCreatingCard(false);
        return;
      }

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Determine API base URL based on environment
      const apiBaseUrl = getCurrentApiUrl();
      
      // STEP 1: Update document expiration date
      const updateDocResponse = await fetch(`${apiBaseUrl}/verestro/update-document-expiration`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ documentExpirationDate: data.documentExpirationDate })
      });

      if (!updateDocResponse.ok) {
        const errorText = await updateDocResponse.text();
        throw new Error(`Failed to update document expiration date: ${updateDocResponse.status}`);
      }

      const updateDocData = await updateDocResponse.json();

      // STEP 2: Create Verestro wallet
      const walletResponse = await fetch(`${apiBaseUrl}/verestro/wallet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({}) // Empty body as per documentation
      });

      if (!walletResponse.ok) {
        const errorText = await walletResponse.text();
        throw new Error(`Failed to create wallet: ${walletResponse.status}`);
      }

      const walletData = await walletResponse.json();

      const verestroUserId = walletData.data?.verestroUserId;
      if (!verestroUserId) {
        throw new Error("Failed to get verestroUserId from wallet creation response");
      }

      // STEP 3: Accept terms and conditions
      const termsResponse = await fetch(`${apiBaseUrl}/verestro/accept-terms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accepted: true,
          termsVersion: "1.0"
        })
      });

      if (!termsResponse.ok) {
        const errorText = await termsResponse.text();
        throw new Error(`Failed to accept terms and conditions: ${termsResponse.status}`);
      }

      const termsData = await termsResponse.json();

      // STEP 4: Send verification
      const verificationResponse = await fetch(`${apiBaseUrl}/verestro/verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!verificationResponse.ok) {
        const errorText = await verificationResponse.text();
        throw new Error(`Failed to send verification: ${verificationResponse.status}`);
      }

      const verificationData = await verificationResponse.json();

      // STEP 5: Create balance with specified currency
      console.log("Step 5: Creating balance...");
      const balanceResponse = await fetch(`${apiBaseUrl}/verestro/balances`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ currency: data.currency || 'EUR' })
      });

      if (!balanceResponse.ok) {
        const errorText = await balanceResponse.text();
        throw new Error(`Failed to create balance: ${balanceResponse.status}`);
      }

      const balanceData = await balanceResponse.json();

      const balanceId = balanceData.data?.balanceId;
      if (!balanceId) {
        throw new Error("Failed to get balanceId from balance creation response");
      }

      // STEP 6: Create MasterCard with the balance ID
      const cardResponse = await fetch(`${apiBaseUrl}/verestro/cards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ balanceId })
      });

      if (!cardResponse.ok) {
        const errorText = await cardResponse.text();
        throw new Error(`Failed to create MasterCard: ${cardResponse.status}`);
      }

      const cardData = await cardResponse.json();

      if (!cardData.success) {
        throw new Error("Card creation failed: " + cardData.message);
      }

      const cardId = cardData.data?.cardId;
      if (!cardId) {
        throw new Error("Failed to get cardId from card creation response");
      }

      // Register the new card
      setNewlyCreatedCardId(cardId);
      setUserHasCard(true);
      setCardDetailsFromDB(cardData.data);

      // Update cache with the new card
      saveCardDataToCache(true, cardData.data, balances, currencies);

      // Hide form
      setShowMastercardForm(false);

      // Update data
      toast.success(`MasterCard created successfully!`);

      // Update both queries
      refetchUserCard();
      queryClient.invalidateQueries({ queryKey: ['user-mastercard-card', userId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      // Get the balance of the new card
      fetchCardBalanceFromAPI(cardId);

      // Clear query cache related to force update
      queryClient.removeQueries({ queryKey: ['verestro-cards'] });
      queryClient.removeQueries({ queryKey: ['verestro-cards-list'] });
      queryClient.removeQueries({ queryKey: ['mastercard-cards'] });

    } catch (error: any) {
      // Parse the error response to show more specific error messages
      let errorMessage = 'Failed to create MasterCard';
      
      try {
        if (error.message.includes('422')) {
          // Try to parse the response for Verestro validation errors
          if (error.response) {
            const errorData = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            } else if (errorData.error) {
              // Handle specific error statuses
              if (errorData.error.status === 'INVALID_POST_CODE') {
                errorMessage = 'Invalid postal code format. Please verify your address information.';
              } else if (errorData.error.status === 'FIELD_IS_TOO_LONG') {
                errorMessage = 'Some information is too long. Please check your details.';
              } else if (errorData.error.status === 'INVALID_EMAIL') {
                errorMessage = 'Invalid email address format.';
              } else if (errorData.error.status === 'INVALID_PHONE') {
                errorMessage = 'Invalid phone number format.';
              } else if (errorData.error.status === 'MISSING_REQUIRED_FIELD') {
                errorMessage = 'Missing required information. Please complete your profile.';
              } else {
                errorMessage = errorData.error.status || 'Validation error occurred';
              }
            }
          }
        } else {
          errorMessage = error.message || 'An unexpected error occurred';
        }
      } catch (parseError) {
        // If we can't parse the error, use the original message
        errorMessage = error.message || 'Failed to create MasterCard';
      }
      
      toast.error(errorMessage);
      console.error('MasterCard creation error:', error);
    } finally {
      setCreatingCard(false);
    }
  };

  // Get currency symbol based on code
  const getCurrencySymbol = (currencyCode: string): string => {
    switch (currencyCode) {
      case 'EUR':
        return '€';
      case 'USD':
        return '$';
      case 'GBP':
        return '£';
      default:
        return currencyCode;
    }
  };

  // Log current state for debugging

  // Handle initial loading state
  if ((!hasCheckedCardExistence && isCheckingDatabase) || !userId || userId === 'guest') {
    return (
      <div>
        <h2 className="wallet-section-title">{t('cards.title')}</h2>
        <div className="wallet-cards-container">
          <div className="wallet-card">
            <div className="wallet-card-icon">
              <MastercardIcon size={36} />
            </div>
            <div className="wallet-card-name">
              MasterCard
            </div>
            <div className="wallet-card-verification">
              {t('cards.checkingStatus')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Determine what to display based on states
  let cardDisplay;

  // If there are available cards
  if (userHasCard && cardDetailsFromDB) {
    // Get balance and currency
    const cardId = cardDetailsFromDB.id;
    const cardBalance = balances[cardId] || '0';
    const cardCurrency = currencies[cardId] || 'EUR';
    const currencySymbol = getCurrencySymbol(cardCurrency);

    // Show card with balance
    cardDisplay = (
      <Link
        to={`/dashboard/cards/${cardId}`}
        className="wallet-card-balance"
      >
        {parseFloat(cardBalance).toLocaleString()} {currencySymbol}
      </Link>
    );
  }
  // If the card is currently being created
  else if (newlyCreatedCardId) {
    console.log(`Card is being created: ${newlyCreatedCardId}`);
    cardDisplay = (
      <div className="wallet-card-creating">
        {t('cards.creatingCard')}
      </div>
    );
  }
  // If no card exists, show creation button
  else {
    cardDisplay = (
      <button
        className={`wallet-create-btn ${(creatingCard || isCheckingMastercardData) ? 'wallet-create-btn-disabled' : ''}`}
        onClick={() => checkMastercardRequirements()}
        disabled={creatingCard || isCheckingMastercardData}
      >
        {creatingCard || isCheckingMastercardData
          ? t('common.processing')
          : t('cards.orderCard')}
      </button>
    );
  }

  return (
    <div >
      <h2 className="wallet-section-title">Cards Wallets</h2>
      <div className="flex flex-wrap justify-center sm:justify-start">
        <div className={`wallet-cards-container ${windowWidth < 376 ? "w-full" : ""}`} >
          {/* Only show one generic card */}
          <div className={`wallet-card ${newlyCreatedCardId ? 'newly-created' : ''}`}>
            <div className="wallet-card-icon h-full">
              <MastercardIcon size={36} />
            </div>
            <div className="wallet-card-name !font-bold">
              Kanz MasterCard
            </div>

            {cardDisplay}
          </div>
        </div>

      </div>
      {/* Form to complete MasterCard data */}
      {showMastercardForm && (
        <MastercardDataCompletionForm
          formData={mastercardFormData}
          onSubmit={handleMastercardFormSubmit}
          onCancel={() => setShowMastercardForm(false)}
          isLoading={creatingCard || isCheckingMastercardData}
        />
      )}

      {/* Requirements Modal */}
      {showRequirementsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Complete Your Profile</h3>
            <p className="mb-4">To create a MasterCard, please complete the following:</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              {missingRequirements.map((req, index) => (
                <li key={index} className="text-red-600">{req}</li>
              ))}
            </ul>
            <div className="flex gap-4">
              <Link
                to="/dashboard/profile"
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded text-center hover:bg-blue-700"
              >
                Go to Profile
              </Link>
              <button
                onClick={() => setShowRequirementsModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terms and Conditions Modal */}
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onAccept={async () => {
          setShowTermsModal(false);
          // After accepting terms, start the automatic card creation
          await startAutomaticCardCreation();
        }}
        onDecline={() => {
          setShowTermsModal(false);
          toast.info('You must accept the terms and conditions to create a MasterCard');
        }}
        isLoading={showBlockingOverlay}
      />

      {/* Blocking Overlay during card creation */}
      {showBlockingOverlay && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mb-6">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
              <h3 className="text-xl font-bold mb-4">Creating Your MasterCard</h3>
              <p className="mb-4 text-gray-600">{creationMessage}</p>

              {/* Progress steps */}
              <div className="space-y-2 text-left max-w-xs mx-auto">
                <div className={`flex items-center gap-2 ${creationStep >= 1 ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className={`w-5 h-5 rounded-full border-2 ${creationStep >= 1 ? 'border-green-600 bg-green-100' : 'border-gray-300'}`}></span>
                  <span>1. Create wallet</span>
                </div>
                <div className={`flex items-center gap-2 ${creationStep >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className={`w-5 h-5 rounded-full border-2 ${creationStep >= 2 ? 'border-green-600 bg-green-100' : 'border-gray-300'}`}></span>
                  <span>2. Accept terms</span>
                </div>
                <div className={`flex items-center gap-2 ${creationStep >= 3 ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className={`w-5 h-5 rounded-full border-2 ${creationStep >= 3 ? 'border-green-600 bg-green-100' : 'border-gray-300'}`}></span>
                  <span>3. Verify KYC</span>
                </div>
                <div className={`flex items-center gap-2 ${creationStep >= 4 ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className={`w-5 h-5 rounded-full border-2 ${creationStep >= 4 ? 'border-green-600 bg-green-100' : 'border-gray-300'}`}></span>
                  <span>4. Create balance</span>
                </div>
                <div className={`flex items-center gap-2 ${creationStep >= 5 ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className={`w-5 h-5 rounded-full border-2 ${creationStep >= 5 ? 'border-green-600 bg-green-100' : 'border-gray-300'}`}></span>
                  <span>5. Issue card</span>
                </div>
              </div>

              <p className="mt-6 text-sm text-gray-500">Please do not close this window...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}